import { useEffect, useState } from 'react';
import { Stack } from '@gafe/common-ui';
import { filter, find, sumBy } from 'lodash';

import { BomLink } from '@/components/generic';
import {
  getOrderItemPrice,
  isAsphaltShingle,
  isEquipment,
  isInventory,
  isRidgeCap,
  isTimberlineSolar,
} from '@/components/modals/SubmitOrder/constants';
import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';
import { OrderItemWrapper } from '@/components/modals/SubmitOrder/types';
import { ProductWithBom } from '@/controllers/Activity';
import { PriceBookEntryVm, ProductType } from '@/types';
import { formatPriceFixed } from '@/utils/format-price';

import { OrderBlock } from '../../../Common/OrderBlock';

import { GenericEquipmentSection } from './components/GenericEquipmentSection';
import { RidgeCapSection } from './components/RidgeCapSection';
import { ShingleSection } from './components/ShingleSection';
import { getSelectedRidgeCap } from './constants';
import { Props } from './types';

import styles from './EquipmentDetails.module.scss';

const justification = { content: 'space-between' };
const orientation = { desktop: 'horizontal' } as const;

export const EquipmentDetails = ({
  productLineName,
  proposalItems,
  opportunity,
  allPriceBookEntries,
  proposalBom,
}: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const [isShingleQuantitySelected, setIsShingleQuantitySelected] = useState(false);
  const getAsphaltShingle = (items: OrderItemWrapper[]) => find(items, oi => isAsphaltShingle(oi));

  useEffect(() => {
    const asphaltShingle = getAsphaltShingle(state.visibleOrderItems);
    const quantity = asphaltShingle?.orderItem.quantity ?? 0;

    setIsShingleQuantitySelected(quantity > 0);
  }, [state.visibleOrderItems]);

  const selectAsphaltShingle = (
    asphaltShingle: OrderItemWrapper | null,
    esShingleQuantity = state.esShingleQuantity,
  ): void => {
    const isRidgeCapRequired = Boolean(asphaltShingle?.priceBookEntry?.product?.dependentProductId);

    const filteredItems = filter(state.visibleOrderItems, oi => {
      if (isAsphaltShingle(oi)) {
        return false;
      }

      // filter out ridge cap items if selected asphalt shingle
      // does not require it
      return !(!isRidgeCapRequired && isRidgeCap(oi));
    });

    const hiddenItems = filter(
      state.hiddenOrderItems,
      oi => !isRidgeCapRequired && !isRidgeCap(oi),
    );

    const visibleOrderItems = [...filteredItems];

    if (asphaltShingle) {
      visibleOrderItems.push(asphaltShingle);
    }

    dispatch({
      type: 'UPDATE',
      payload: {
        visibleOrderItems,
        hiddenOrderItems: [...hiddenItems],
        isRidgeCapSectionEnabled: isRidgeCapRequired,
        esShingleQuantity,
      },
    });
  };

  const onAsphaltShingleSelection = (
    asphaltShingle: OrderItemWrapper | null,
    esShingleQuantity?: number,
  ) => {
    if (!asphaltShingle) {
      selectAsphaltShingle(null, esShingleQuantity);
      setIsShingleQuantitySelected(false);

      return;
    }

    if (asphaltShingle.orderItem) {
      selectAsphaltShingle(asphaltShingle);
    }

    setIsShingleQuantitySelected(asphaltShingle.orderItem?.quantity > 0);
  };

  const selectedEquipment = filter(
    state.visibleOrderItems,
    oi => isEquipment(oi) && !isInventory(oi),
  );
  const getAllPriceBookEntries = (): PriceBookEntryVm[] => {
    if (!opportunity) {
      return allPriceBookEntries;
    }

    // get only those price book entries that belong to
    // the price book of the current opportunity

    return filter(allPriceBookEntries, pbe => pbe.priceBookId === opportunity?.priceBookId);
  };

  const availableAsphaltShingles: PriceBookEntryVm[] = filter(
    getAllPriceBookEntries(),
    pbe => pbe.product.type === ProductType['Asphalt Shingle'] && Boolean(pbe.isProductAvailable),
  );

  const selectedAsphaltShingle = find(selectedEquipment, e => isAsphaltShingle(e)) ?? null;

  const selectedGenericEquipment = filter(selectedEquipment, e => {
    const product = e.proposalItem?.product as ProductWithBom | undefined;

    // this is to hide BOM components except for solar single (ES)
    return (
      isTimberlineSolar(product) ||
      (!isAsphaltShingle(e) && !isRidgeCap(e) && product?.isBomComponent !== true)
    );
  });

  const selectedRidgeCap = getSelectedRidgeCap(
    selectedAsphaltShingle,
    selectedEquipment,
    getAllPriceBookEntries(),
    proposalItems,
  );

  const totalPrice = sumBy(selectedEquipment, equipment => getOrderItemPrice(state, equipment));

  // we cannot display current section if there is no generic equipment
  if (selectedGenericEquipment.length === 0) {
    return null;
  }

  if (productLineName === 'Decotech®') {
    return (
      <OrderBlock
        hideValue={state.isPriceHidden}
        title={productLineName}
        value={totalPrice}
      ></OrderBlock>
    );
  }

  const asphaltShingleCost = selectedAsphaltShingle
    ? getOrderItemPrice(state, selectedAsphaltShingle)
    : 0;

  const title = proposalBom ? 'Total Solar and Roofing Materials Cost' : productLineName;
  let orderTotal = totalPrice;

  if (proposalBom) {
    orderTotal = proposalBom.estimatedSolarMaterialsCost + asphaltShingleCost;
    orderTotal += selectedRidgeCap ? getOrderItemPrice(state, selectedRidgeCap) : 0;
  }

  return (
    <OrderBlock hideValue={state.isPriceHidden} title={title} value={orderTotal}>
      <br />
      {proposalBom ? (
        <Stack className={styles.material_cost} justify={justification} orientation={orientation}>
          <BomLink fontSize={16} proposalId={proposalBom.id} />
          <span className={styles.material_cost_value}>
            {formatPriceFixed(proposalBom.estimatedSolarMaterialsCost)}
          </span>
        </Stack>
      ) : (
        <>
          {selectedGenericEquipment.map(oi => (
            <div key={oi.priceBookEntry.productId}>
              <GenericEquipmentSection equipment={oi} />
              <br />
            </div>
          ))}
        </>
      )}
      <ShingleSection
        availableAsphaltShingles={availableAsphaltShingles}
        proposalItems={proposalItems}
        selectAsphaltShingle={onAsphaltShingleSelection}
        selectedAsphaltShingle={selectedAsphaltShingle}
      />
      <br />
      {selectedRidgeCap && isShingleQuantitySelected && (
        <RidgeCapSection selectedRidgeCap={selectedRidgeCap} />
      )}
    </OrderBlock>
  );
};
